import { useState, useCallback } from "react";
import { Building2, Eye, EyeOff } from "lucide-react";
import {
  GoogleReCaptchaProvider,
  useGoogleReCaptcha,
} from "react-google-recaptcha-v3";
import axios from "axios";
import { useAuth } from "../../hooks/useAuth";
import { useTheme } from "../../components/ThemeProvider";
import {
  login,
  respondToChallenge,
  LoginResponse,
  AuthChallengeResponse,
} from "../../requestors/loginRequestor";
import { PasswordRequirements, isPasswordValid } from "./PasswordRequirements";
import { ForgotPasswordFlow } from "./ForgotPasswordFlow";
import { SSOLoginDialog } from "./SSOLoginDialog";
import { broadcastLogin } from "../../utils/authBroadcast";
import datasurfrWhiteLogo from "../../assets/svg/datasurfrWhiteLogo.svg";
import datasurfrDarkLogo from "../../assets/svg/datasurfrDarkLogo.svg";
import loginScreenDark from "../../assets/login.png";
import loginScreenLight from "../../assets/loginLight.png";

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || "";
const CUSTOM_AUTH_KEY = "datasurfr_custom_auth";

type LoginView = "login" | "forgot-password" | "sso" | "new-password-required";

// ─── Token utilities ─────────────────────────────────────────────

export const storeCustomAuthTokens = (tokens: LoginResponse) => {
  sessionStorage.setItem(
    CUSTOM_AUTH_KEY,
    JSON.stringify({
      access_token: tokens.accessToken,
      refresh_token: tokens.refreshToken,
      id_token: tokens.idToken,
      expires_in: tokens.expiresIn,
      token_type: "Bearer",
    }),
  );
};

export const getCustomAuthTokens = (): {
  access_token: string;
  refresh_token: string;
  id_token: string;
  expires_in: number;
  token_type: string;
} | null => {
  const stored = sessionStorage.getItem(CUSTOM_AUTH_KEY);
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

export const clearCustomAuthTokens = () => {
  sessionStorage.removeItem(CUSTOM_AUTH_KEY);
};

export const hasCustomAuth = (): boolean => {
  return getCustomAuthTokens() !== null;
};

// ─── Helpers ─────────────────────────────────────────────────────

function isDarkTheme(theme: "dark" | "light" | "system") {
  if (theme === "dark") return true;
  if (theme === "system")
    return document.documentElement.classList.contains("dark");
  return false;
}

const LoadingSpinner = () => (
  <svg
    className="animate-spin h-5 w-5"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
  >
    <circle
      className="opacity-25"
      cx="12"
      cy="12"
      r="10"
      stroke="currentColor"
      strokeWidth="4"
    />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
    />
  </svg>
);

// ─── Shared style helpers ────────────────────────────────────────

const INPUT_CLASS =
  "w-full border rounded-lg outline-none transition-shadow focus:ring-2 focus:ring-login-accent focus:border-login-accent login-input bg-login-input-bg border-login-input-border text-login-input-text";

const PRIMARY_BTN_CLASS =
  "w-full rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 flex items-center justify-center login-btn bg-login-btn-bg text-login-btn-text";

// ─── Login page content ─────────────────────────────────────────

const LoginContent = () => {
  const auth = useAuth();
  const { theme } = useTheme();
  const isDark = isDarkTheme(theme);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [hasCredentialsError, setHasCredentialsError] = useState(false);
  const [view, setView] = useState<LoginView>("login");

  const [challengeSession, setChallengeSession] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  const handleLogin = useCallback(async () => {
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedEmail) {
      setError("Please enter your email or username");
      return;
    }
    if (!password.trim()) {
      setError("Please enter your password");
      return;
    }

    setIsLoading(true);
    setError("");
    setHasCredentialsError(false);

    // Dev bypass: skip API call and go straight to /home
    if (import.meta.env.DEV && import.meta.env.VITE_BYPASS_AUTH === "true") {
      window.location.href = "/home";
      return;
    }

    try {
      let recaptchaToken = "";
      if (executeRecaptcha) {
        recaptchaToken = await executeRecaptcha("login");
      }

      const result = await login(trimmedEmail, password, recaptchaToken);

      if (
        "challengeName" in result &&
        (result as unknown as AuthChallengeResponse).challengeName ===
          "NEW_PASSWORD_REQUIRED"
      ) {
        const challenge = result as unknown as AuthChallengeResponse;
        setChallengeSession(challenge.session);
        setView("new-password-required");
        setIsLoading(false);
        return;
      }

      storeCustomAuthTokens(result);
      // Relay tokens to other open datasurfr tabs so they can auto-login
      try {
        const stored = sessionStorage.getItem(CUSTOM_AUTH_KEY);
        if (stored) broadcastLogin("datasurfr", JSON.parse(stored));
      } catch {
        /* ignore broadcast errors */
      }
      window.location.href = "/home";
    } catch (err) {
      setIsLoading(false);

      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        const errorData = err.response?.data;

        if (errorData?.error === "INVALID_CREDENTIALS" || status === 401) {
          setError(errorData?.message || "Invalid email or password.");
        } else if (errorData?.error === "ACCOUNT_DISABLED") {
          setError(
            "Your account has been disabled. Please contact your administrator.",
          );
        } else if (errorData?.error === "RATE_LIMIT" || status === 429) {
          setError("Too many login attempts. Please try again later.");
        } else if (errorData?.error === "RECAPTCHA_FAILED") {
          setError("reCAPTCHA verification failed. Please try again.");
        } else if (errorData?.error === "MFA_REQUIRED") {
          setError(
            "Multi-factor authentication is required. Redirecting to secure login...",
          );
          setTimeout(() => auth.signinRedirect(), 2000);
        } else if (errorData?.message) {
          setError(errorData.message);
        } else {
          setError("Login failed. Please try again.");
        }
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    }
  }, [email, password, executeRecaptcha, auth]);

  const handleChallengeSubmit = useCallback(async () => {
    if (!isPasswordValid(newPassword)) {
      setError("Password does not meet all requirements");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const tokens = await respondToChallenge(
        "NEW_PASSWORD_REQUIRED",
        challengeSession,
        newPassword,
        email.trim().toLowerCase(),
      );
      storeCustomAuthTokens(tokens);
      // Relay tokens to other open datasurfr tabs so they can auto-login
      try {
        const stored = sessionStorage.getItem(CUSTOM_AUTH_KEY);
        if (stored) broadcastLogin("datasurfr", JSON.parse(stored));
      } catch {
        /* ignore broadcast errors */
      }
      window.location.href = "/home";
    } catch (err) {
      setIsLoading(false);
      if (axios.isAxiosError(err)) {
        setError(
          err.response?.data?.message ||
            "Failed to set new password. Please try again.",
        );
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    }
  }, [newPassword, confirmNewPassword, challengeSession, email]);

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === "Enter") action();
  };

  const resetToLogin = () => {
    setView("login");
    setError("");
    setHasCredentialsError(false);
    setNewPassword("");
    setConfirmNewPassword("");
    setChallengeSession("");
  };

  // ─── Error banner ─────────────────────────────────────

  const ErrorBanner = () =>
    error ? (
      <div className="p-3 border rounded-lg px-4 py-2 font-body-primary bg-login-error-bg border-login-error-border text-login-error-text">
        {error}
      </div>
    ) : null;

  // ─── Content views ────────────────────────────────────

  const renderContent = () => {
    if (view === "forgot-password") {
      return (
        <ForgotPasswordFlow
          onBack={resetToLogin}
          initialEmail={email}
          isDark={isDark}
        />
      );
    }

    if (view === "sso") {
      return <SSOLoginDialog onBack={resetToLogin} />;
    }

    // NEW_PASSWORD_REQUIRED challenge
    if (view === "new-password-required") {
      return (
        <div className="flex flex-col gap-3">
          <div>
            <h1 className="font-semibold font-heading-primary text-login-heading">
              Set new password
            </h1>
            <p className="mt-1 font-normal font-body-primary text-login-subtext">
              Your account requires a new password. Please set one below.
            </p>
          </div>

          <ErrorBanner />

          <div>
            <label className="block font-medium mb-1 font-body-primary text-login-label">
              New password <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              autoFocus
              className={INPUT_CLASS}
            />
          </div>

          <PasswordRequirements password={newPassword} isDark={isDark} />

          <div>
            <label className="block font-medium mb-1 font-body-primary text-login-label">
              Confirm new password <span className="text-red-500">*</span>
            </label>
            <input
              type="password"
              value={confirmNewPassword}
              onChange={(e) => setConfirmNewPassword(e.target.value)}
              placeholder="Confirm new password"
              className={INPUT_CLASS}
              onKeyDown={(e) => handleKeyDown(e, handleChallengeSubmit)}
            />
          </div>

          {newPassword &&
            confirmNewPassword &&
            newPassword !== confirmNewPassword && (
              <p className="text-red-500 font-body-primary">
                Passwords do not match
              </p>
            )}

          <button
            onClick={handleChallengeSubmit}
            disabled={
              isLoading ||
              !isPasswordValid(newPassword) ||
              newPassword !== confirmNewPassword
            }
            className={PRIMARY_BTN_CLASS}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <LoadingSpinner />
                Setting password...
              </span>
            ) : (
              "Set password & sign in"
            )}
          </button>
        </div>
      );
    }

    // ─── Main login form ────────────────────────────────

    return (
      <div className="flex flex-col gap-3">
        <div>
          <h1 className="font-semibold font-heading-primary text-login-heading">
            Sign in
          </h1>
          <p className="mt-1 font-normal font-body-primary text-login-subtext">
            to continue to DataSurfr
          </p>
        </div>

        <ErrorBanner />

        {/* Email */}
        <div>
          <label className="block font-medium mb-1 font-body-primary text-login-label">
            Email or Username <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@company.com or username"
            autoFocus
            className={INPUT_CLASS}
            onKeyDown={(e) => handleKeyDown(e, handleLogin)}
          />
        </div>

        {/* Password */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block font-medium font-body-primary text-login-label">
              Password <span className="text-red-500">*</span>
            </label>
            <button
              onClick={() => {
                setError("");
                setView("forgot-password");
              }}
              className="font-medium transition-colors hover:opacity-80 font-body-primary text-login-accent"
            >
              Forgot password?
            </button>
          </div>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className={`${INPUT_CLASS} login-input-password`}
              onKeyDown={(e) => handleKeyDown(e, handleLogin)}
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 transition-colors text-login-subtext"
              tabIndex={-1}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? (
                <EyeOff className="login-icon" />
              ) : (
                <Eye className="login-icon" />
              )}
            </button>
          </div>
        </div>

        {/* Sign in button */}
        <button
          onClick={handleLogin}
          disabled={isLoading || !email.trim() || !password.trim()}
          className={PRIMARY_BTN_CLASS}
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <LoadingSpinner />
              Signing in...
            </span>
          ) : hasCredentialsError ? (
            "Try Again"
          ) : (
            "Sign in"
          )}
        </button>

        {/* Divider */}
        <div className="flex items-center gap-3 my-1">
          <div className="flex-1 h-px bg-login-divider" />
          <span className="uppercase tracking-wider font-normal font-body-secondary text-login-muted">
            or
          </span>
          <div className="flex-1 h-px bg-login-divider" />
        </div>

        {/* SSO button */}
        <button
          onClick={() => {
            setError("");
            setView("sso");
          }}
          className="w-full border rounded-md font-medium transition-colors flex items-center justify-center gap-2 hover:opacity-80 login-btn bg-transparent border-login-sso-border text-login-sso-text"
        >
          <Building2 className="login-icon" />
          Sign in with SSO
        </button>

        <p className="text-center mt-1 font-body-secondary text-login-muted">
          Protected by reCAPTCHA.{" "}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:opacity-80"
          >
            Privacy
          </a>
          {" & "}
          <a
            href="https://policies.google.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:opacity-80"
          >
            Terms
          </a>
        </p>
      </div>
    );
  };

  return (
    <div
      style={{
        backgroundImage: `url(${isDark ? loginScreenDark : loginScreenLight})`,
      }}
      className="w-screen h-screen flex flex-col items-center justify-center p-4 bg-cover bg-center bg-no-repeat"
    >
      {/* Card */}
      <div className="w-full login-card rounded-2xl border p-5 sm:p-8 bg-login-card-bg border-login-card-border shadow-login-card dark:backdrop-blur-[24px]">
        {/* Logo */}
        <div className="mb-4">
          <img
            src={isDark ? datasurfrWhiteLogo : datasurfrDarkLogo}
            alt="DataSurfr"
            className="login-logo"
          />
        </div>

        {renderContent()}
      </div>

      {/* Footer */}
      <p className="mt-6 font-body-secondary text-login-footer">
        &copy; {new Date().getFullYear()} DataSurfr. All rights reserved.
      </p>
    </div>
  );
};

// ─── Exported LoginBox with reCAPTCHA provider ──────────────────

export const LoginBox = () => {
  return RECAPTCHA_SITE_KEY ? (
    <GoogleReCaptchaProvider reCaptchaKey={RECAPTCHA_SITE_KEY}>
      <LoginContent />
    </GoogleReCaptchaProvider>
  ) : (
    <LoginContent />
  );
};
